import React, { useEffect, useMemo, useState } from 'react';
import { useContentApi } from '../../features/auth/hooks/use-content-api.ts';
import { ApiResponseType } from '../../features/api/types.ts';
import { useProfileContext } from '../../features/auth/hooks/use-profile-context.ts';
import { marked } from 'marked';
import '../../scss/components/DynamicContent.scss';

marked.setOptions({
    gfm: true,
    breaks: true,
});

interface DynamicContentProps {
    title: string;
    // Optional {{key}} placeholders replaced in the note body before rendering.
    replacements?: { [key:string]: string };
}

export default function DynamicContent({ title, replacements }: DynamicContentProps) {
    const [rawMarkdown, setRawMarkdown] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const { getContent } = useContentApi();
    const { profile } = useProfileContext();

    // Placeholders available in every note; a caller's replacements take precedence.
    const allReplacements:{ [key:string]: string } = {
        contact_number: profile?.real_number ?? '',
        ...(replacements ?? {}),
    };

    useEffect(() => {
        getContent(title).then(data => {
            if (data.type === ApiResponseType.OK && data.payload?.content) {
                setRawMarkdown(String(data.payload.content)
                    .replace(/\r\n/g, '\n')
                    .replace(/\r/g, '\n'));
            } else {
                setError(true);
            }
            setIsLoading(false);
        });
    }, [title]);

    // Substitute {{key}} placeholders (re-runs when the values become available),
    // then render Markdown. An unknown value collapses to an empty string.
    const content = useMemo(() => {
        if (!rawMarkdown) {
            return '';
        }
        // The CMS editor may Markdown-escape characters inside tokens (e.g. underscores:
        // {{exam_date}} stored as {{exam\_date}}). Un-escape within {{…}} before matching.
        let markdown = rawMarkdown.replace(/\{\{(.*?)\}\}/g, (_m, inner) => '{{' + inner.replace(/\\(.)/g, '$1') + '}}');
        Object.entries(allReplacements).forEach(([key, value]) => {
            markdown = markdown.split('{{' + key + '}}').join(value ?? '');
        });
        return marked.parse(markdown) as string;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawMarkdown, JSON.stringify(allReplacements)]);

    if (isLoading) {
        return <div className="loader"></div>;
    }

    if (error) {
        return null;
    }

    return (
        <div
            className="dynamic-content"
            dangerouslySetInnerHTML={{ __html: content }}
        />
    );
}
